import { DialogTitle } from "@mui/material";
import { stellaInspectionNoteBusiness } from "api/bussiness/inspection-note/stella-inspection-note/stella-inspection-note-business";
import { productBussiness } from "api/bussiness/product/product-bussiness";
import { workOrderPoBusiness } from "api/bussiness/work-order-po/work-order-po-bussiness";
import { InspectionConclusionEnum } from "commons/enum/inspection";
import { dateTimeToUTCString } from "commons/ultilities/date/dayjs";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { DynamicFormArray } from "commons/ultilities/dynamic-form/components/dynamic-form-array";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import {
  IControl,
  IControlProperties,
} from "commons/ultilities/dynamic-form/models/control";
import { createProductOptionLabel } from "commons/ultilities/option-item/label-ultil";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import { observer } from "mobx-react-lite";
import { IAddStellaInspectionNote } from "mobx-stores/_models/inspection-note/stella-inspection-note/stella-inspection-note";
import { IAddStellaInspectionNoteItem } from "mobx-stores/_models/inspection-note/stella-inspection-note/stella-inspection-note-item";
import { IProductProperties } from "mobx-stores/_models/products/product";
import { IWorkOrderPoProperties } from "mobx-stores/_models/work-order-po/work-order-po";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useMemo } from "react";
import { DialogComplexContent } from "share-components/templates/form/dialog-complex-form/dialog-complex-content";
import { DialogComplexFormContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form";
import { DialogComplexFormArrayContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form-array";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getAddInspectionNoteFormSchema } from "../schema";
import { conclusionDefautText, createQuantification } from "../share";

export const AddStellaInspectionNote = observer(() => {
  const {
    stellaInspectionNoteManagement: { openNew, setProp },
    inspectionNoteManagement: { getInspectionNotes },
    setProp: setRootStoreProp,
  } = useStores();
  const {
    form: { controls, isValid },
    getControl,
    getControlArray,
    submitHandler,
  } = useForm<IAddStellaInspectionNote>(
    (form) => getAddInspectionNoteFormSchema(form),
    {
      number: "",
      expireDate: "",
      inspectionDate: dateTimeToUTCString(dayjs()),
      manufacturingDate: "",
      workOrderPOId: "",
      customerId: "",
      conclusion: conclusionDefautText,
      items: [
        {
          amount: "",
          productId: "",
          appearance: "",
          appearanceConclusion: InspectionConclusionEnum.None,
          appearanceResult: "",
          foil: "",
          foilResult: 0,
          foilConclusion: InspectionConclusionEnum.None,
          height: 0,
          heightConclusion: InspectionConclusionEnum.None,
          heightResult: 0,
          length: 0,
          lengthConclusion: InspectionConclusionEnum.None,
          lengthResult: 0,
          quantification: "",
          quantificationConclusion: InspectionConclusionEnum.None,
          quantificationResult: 0,
          width: 0,
          widthConclusion: InspectionConclusionEnum.None,
          widthResult: 0,
          printPaperSP: 0,
          foldingStandardCountConclusion: InspectionConclusionEnum.None,
          foldingStandardCountResult: "",
          foldingStandardSizeConclusion: InspectionConclusionEnum.None,
          foldingStandardSizeResult: "",
        },
      ],
    },
  );

  const saveHandler = useCallback(
    async (data: IAddStellaInspectionNote) => {
      try {
        await stellaInspectionNoteBusiness.add(data);
        // await getStellaInspectionNotes();
        await getInspectionNotes();
        setProp("openNew", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [getInspectionNotes, setProp],
  );

  const itemControl = useMemo(() => {
    return getControl("items");
  }, [getControl]);

  const inspectionNoteItems = useMemo(() => {
    return getControlArray("items");
  }, [getControlArray]);

  const cleanItems = useCallback(() => {
    if (itemControl) {
      itemControl.setProp("children", []);
    }
  }, [itemControl]);

  const setOptionItemsForProductIsEmty = useCallback(() => {
    if (itemControl) {
      const newChildSchema = cloneDeep(itemControl.childSchema);
      const productIdSchema = newChildSchema.productId as IControlProperties;
      productIdSchema.optionItems = [];

      itemControl.setChildSchema(newChildSchema);
    }
  }, [itemControl]);

  const setOptionItemsForProduct = useCallback(
    (wo: IWorkOrderPoProperties) => {
      if (itemControl) {
        const newChildSchema = cloneDeep(itemControl.childSchema);
        const productIdSchema = newChildSchema.productId as IControlProperties;
        const product = wo.purchaseOrderItem?.product;
        productIdSchema.optionItems = product
          ? [
              {
                label: createProductOptionLabel(product as IProductProperties),
                value: product.id,
              },
            ]
          : [];

        itemControl.setChildSchema(newChildSchema);
      }
    },
    [itemControl],
  );

  const setCustomerSelectionIsEmty = useCallback(() => {
    const customerIdCtr = getControl("customerId");
    customerIdCtr.setProp("optionItems", []);
    customerIdCtr.setProp("value", "");
  }, [getControl]);

  const setOptionItemsForCustomer = useCallback(
    (wo: IWorkOrderPoProperties) => {
      const customerIdCtr = getControl("customerId");
      // Fallback chain matching the model's customerDisplay view
      const customer =
        wo.purchaseOrder?.customer ??
        wo.purchaseOrderItem?.product?.customer ??
        wo.customer;

      customerIdCtr.setProp("optionItems", [
        {
          value: customer?.id,
          label: customer?.dealingName || "",
        },
      ]);
      customerIdCtr.setProp("value", customer?.id);
    },
    [getControl],
  );

  const valueChangeHandler = useCallback(
    async (control: IControl) => {
      if (control.name === "manufacturingDate") {
        if (control.value && dayjs(control.value).isValid()) {
          getControl("expireDate").setProp(
            "value",
            control.value.add(3, "year"),
          );
        } else {
          getControl("expireDate").setProp("value", "");
        }
      }
      if (control.name === "workOrderPOId") {
        try {
          setRootStoreProp("appFreeze", true);
          if (control.value) {
            cleanItems();
            setOptionItemsForProductIsEmty();
            setCustomerSelectionIsEmty();
            const wo = await workOrderPoBusiness.getById(control.value);
            setOptionItemsForCustomer(wo);
            setOptionItemsForProduct(wo);

            const woProductId = wo.purchaseOrderItem?.productId;
            if (woProductId) {
              const product = await productBussiness.getById(woProductId);
              itemControl.addChildren<IAddStellaInspectionNoteItem>([
                {
                  productId: woProductId,
                  amount: "",
                  length: product?.dimensionWeight?.lengthStandard || 0,
                  lengthResult: 0,
                  appearanceConclusion: InspectionConclusionEnum.None,
                  appearanceResult: "",
                  height: product?.dimensionWeight?.heightStandard || 0,
                  heightResult: 0,
                  heightConclusion: InspectionConclusionEnum.None,
                  width: product?.dimensionWeight?.widthStandard || 0,
                  widthResult: 0,
                  widthConclusion: InspectionConclusionEnum.None,
                  foil: product.process?.foil || "",
                  foilResult: 0,
                  foilConclusion: InspectionConclusionEnum.None,
                  lengthConclusion: InspectionConclusionEnum.None,
                  quantification: createQuantification(product),
                  quantificationConclusion: InspectionConclusionEnum.None,
                  quantificationResult: 0,
                  printPaperSP: product.process?.printPaperSP || 0,
                  appearance: "",
                  foldingStandardCountConclusion: InspectionConclusionEnum.None,
                  foldingStandardCountResult: "",
                  foldingStandardSizeConclusion: InspectionConclusionEnum.None,
                  foldingStandardSizeResult: "",
                  foldingStandardSize:
                    product?.dimensionWeight?.foldingStandardSize || "",
                  foldingStandardCount:
                    product?.dimensionWeight?.foldingStandardCount || "",
                },
              ]);
            }
          }
        } catch (ex) {
        } finally {
          setRootStoreProp("appFreeze", false);
        }
      }
    },
    [
      cleanItems,
      getControl,
      itemControl,
      setCustomerSelectionIsEmty,
      setOptionItemsForCustomer,
      setOptionItemsForProduct,
      setOptionItemsForProductIsEmty,
      setRootStoreProp,
    ],
  );

  return (
    <DialogForm
      open={openNew}
      fullWidth
      maxWidth={"xl"}
      onClose={() => setProp("openNew", false)}
      onCancelBtnClicked={() => setProp("openNew", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Thêm phiếu kiểm nghiệm</DialogTitle>
      <DialogComplexContent>
        <DialogComplexFormContainer>
          <DynamicForm
            controls={controls}
            onValueChanged={valueChangeHandler}
          ></DynamicForm>
        </DialogComplexFormContainer>
        <DialogComplexFormArrayContainer label={itemControl.label}>
          {inspectionNoteItems && inspectionNoteItems.length > 0 && (
            <DynamicFormArray
              containerProps={{
                sx: {
                  marginTop: "10px",
                  marginBottom: "10px",
                },
                spacing: 2,
              }}
              controlSpacing={2}
              children={inspectionNoteItems}
            ></DynamicFormArray>
          )}
        </DialogComplexFormArrayContainer>
      </DialogComplexContent>
    </DialogForm>
  );
});
